import type { ReactNode } from 'react'

import { lanesFitBesideEachOther, useLayoutStep } from './layout'

/**
 * The layout rule, applied.
 *
 * An area hands in an overview and, when something inside it is open, a detail. Whether those two
 * stand beside each other or follow one another is decided here — once, from `layout.ts` — and not
 * by the area. Five areas each deciding for themselves would produce five readings of what "back"
 * means.
 *
 * When the detail takes the whole lane, the way back is the frame's back control in the bar. It is
 * in the same place in every area and it always removes the last step of the address, so it works
 * whether you walked here or arrived on a link somebody sent you.
 */
export interface Pane {
  /** Names the region for a screen reader, and is not otherwise shown. */
  readonly label: string
  readonly content: ReactNode
}

export interface LanesProps {
  readonly overview: Pane
  /** What is open inside the overview, when something is. */
  readonly detail?: Pane | null
  /**
   * The detail takes the whole frame, however much room there is.
   *
   * Asked for by the area rather than decided here, because it is not about how much room there is —
   * it is about what somebody is doing. Writing is the case it exists for: a folder of tiles beside
   * the text is a folder of tiles somebody is not looking at, and it costs the words half the width.
   * The way back is the frame's own back control, exactly as it is on a narrow screen.
   */
  readonly full?: boolean
}

export function Lanes({ overview, detail = null, full = false }: LanesProps) {
  const step = useLayoutStep()
  const bothFit = lanesFitBesideEachOther(step) && !full
  const split = detail !== null && bothFit

  // Only what is on the screen is in the document: a lane that is merely hidden is still in the
  // reading order, and a screen reader would walk through an overview nobody can see.
  const showOverview = detail === null || bothFit

  return (
    <div
      className={['lanes', split ? 'lanes--split' : null, full ? 'lanes--full' : null]
        .filter((one) => one !== null)
        .join(' ')}
    >
      {showOverview && (
        <section className="lane lane--overview" aria-label={overview.label}>
          {overview.content}
        </section>
      )}
      {detail !== null && (
        <section className="lane lane--detail" aria-label={detail.label}>
          {detail.content}
        </section>
      )}
    </div>
  )
}
